import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { VonMessageService } from '@von-development-studio/primeng-message-service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { RestService } from 'src/app/core/service/rest.service';
import { AdminSubCatalogService } from 'src/app/main/admin/subcatalog/admin-subcatalog.service';
import { CommonDoctorViewModel } from 'src/app/model/common/doctor.view';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { CommonTurnModel } from 'src/app/model/common/turn';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { SecurityOptionProfileView } from 'src/app/model/security/option-profile';
import { SecurityUserLoginModel } from 'src/app/model/security/security-user.login';
import { commonTurnActions } from 'src/app/store/common/turn/turn.action';
import { environment } from 'src/environments/environment';
import { TURN } from '../turn.constants';

@Injectable({
  providedIn: 'root',
})
export class TurnDialogService extends BaseService {
  readonly screen = TURN;

  constructor(
    protected store: Store<any>,
    protected rest: RestService,
    protected subCatalogService: AdminSubCatalogService,
    protected messageService: VonMessageService
  ) {
    super();
  }

  readStore = () => {
    const specialties = this.subCatalogService.getSubCatalogStore('spe');
    const turnStatus = this.subCatalogService.getSubCatalogStore('turn_status');
    return { specialties, turnStatus };
  };

  getFormSavedStore = (): Observable<{
    turn: CommonTurnViewModel;
    formSaved: boolean;
  }> => this.store.select('common', 'turn');

  getScreenOptions = (): Observable<SecurityOptionProfileView[]> =>
    this.store
      .select('admin', 'user')
      .pipe(map((user: SecurityUserLoginModel) => user.options!));

  readPatients = (query: string): Observable<CommonPatientViewModel[]> =>
    this.rest.get<CommonPatientViewModel[]>({
      url: `${environment.endpoints.common}/patient/byQuery`,
      params: { query },
    });

  readDoctorBySpecialty = (
    specialty: string
  ): Observable<CommonDoctorViewModel[]> =>
    this.rest.get<CommonDoctorViewModel[]>({
      url: `${environment.endpoints.common}/doctor/bySpecialty/${specialty}`,
    });

  readAllTurns = (): Observable<CommonTurnViewModel[]> =>
    this.rest.get<CommonTurnViewModel[]>({
      url: `${environment.endpoints.common}/turn/agenda`,
    });

  saveNew = (
    turn: CommonTurnModel,
    isAgenda: boolean = false
  ): Observable<CommonTurnViewModel> =>
    this.rest
      .post<CommonTurnViewModel>({
        url: `${environment.endpoints.common}/turn`,
        body: turn,
      })
      .pipe(
        tap((result) => {
          if (!isAgenda) {
            this.store.dispatch(commonTurnActions.addResultAction(result));
          }
          this.messageService.addSuccess(this.screen.labels.newSuccess);
          this.setFormSaved({
            ...result,
            isDoctorRole: turn.isDoctorRole,
            turnBalance: turn.balance,
          });
        })
      );

  patchStatus = (
    id: number,
    newStatus?: string,
    status: boolean = true
  ): Observable<CommonTurnViewModel> =>
    this.rest
      .patch<CommonTurnViewModel>({
        url: `${environment.endpoints.common}/turn/{id}/status/{newStatus}`,
        urlParams: { id, newStatus },
        params: { status },
      })
      .pipe(
        tap((result) => {
          this.store.dispatch(commonTurnActions.patchResultAction(result));
          this.messageService.addSuccess(this.screen.labels.patchSuccess);
          this.setFormSaved(result);
        })
      );

  patchDoctor = (
    id: number,
    newDoctor?: number
  ): Observable<CommonTurnViewModel> =>
    this.rest
      .patch<CommonTurnViewModel>({
        url: `${environment.endpoints.common}/turn/{id}/doctor/{newDoctor}`,
        urlParams: { id, newDoctor },
      })
      .pipe(
        tap((result) => {
          this.store.dispatch(commonTurnActions.patchResultAction(result));
          this.messageService.addSuccess(this.screen.labels.patchSuccess);
          this.setFormSaved(result);
        })
      );

  patchAgenda = (
    id: number,
    turn: CommonTurnModel
  ): Observable<CommonTurnViewModel> =>
    this.rest
      .patch<CommonTurnViewModel>({
        url: `${environment.endpoints.common}/turn/{id}/agenda`,
        body: turn,
        urlParams: { id },
      })
      .pipe(
        tap((result) => {
          this.store.dispatch(commonTurnActions.patchResultAction(result));
          this.messageService.addSuccess(this.screen.labels.patchSuccess);
          this.setFormSaved(result);
        })
      );

  deleteAgenda = (id: number): Observable<any> =>
    this.rest.delete({
      url: `${environment.endpoints.common}/turn/{id}/agenda`,
      urlParams: { id },
    });

  addComment = (id: number, comments: string): Observable<void> =>
    this.rest.put<void>({
      url: `${environment.endpoints.common}/patient/{id}/comments`,
      body: comments,
      urlParams: { id },
    });

  setFormSaved = (turn: CommonTurnViewModel, formSaved: boolean = true) =>
    this.store.dispatch(
      commonTurnActions.saveTurnFormAction({ turn, formSaved })
    );
}
